import { formatNumber, formatCurrency } from 'i18n/format';
import { formatDate as localeDate } from 'i18n/format';
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  useConnection,
  useConnectionActivity,
  useUpdateConnection,
  useDeleteConnection,
  useTestConnection,
} from 'api/connectors';
import { usePipelines, usePipelinesHealth } from 'api/pipelines';
import { takeOAuthDraft } from 'api/oauth';
import OAuthConnectField from 'components/common/OAuthConnectField';
import { extractApiError, hasFieldErrors, normalizeFieldErrors } from 'api/errors';
import AsyncTaskButton from 'components/common/AsyncTaskButton';
import JsonSchemaForm, { formatConfigValue } from 'components/common/JsonSchemaForm';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import Section, { Row } from 'components/pipelines/Section';
import Verdict from 'components/pipelines/Verdict';
import { FAILURE, loadState, STATE } from 'components/pipelines/loadStatus';
import { scheduleText } from 'components/pipelines/schedule';
import { useTokens } from 'theme';
import { plural, whenInline } from 'utils/ru';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';
import { localizeConnectorType } from 'i18n/metadata';

// BigQuery on-demand, US: what the source charges per TiB scanned. An estimate —
// the rate differs by region and edition, so the record keeps bytes, not money.
const USD_PER_TIB = 6.25;
const TIB = 1024 ** 4;

const gib = (bytes) => {
  const g = bytes / 1024 ** 3;
  return g >= 1 ? t('data.valueGib', { value1: formatNumber(g, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }) : t('data.valueMib', { value1: Math.round(bytes / 1024 ** 2) });
};
const usd = (bytes) => {
  const v = (bytes / TIB) * USD_PER_TIB;
  return v < 0.01 ? `< ${formatCurrency(0.01, 'USD')}` : `≈ ${formatCurrency(v, 'USD')}`;
};

const shortDate = (iso) => localeDate(new Date(iso), { day: 'numeric', month: 'short' });

/** "8–10 окт., доступ отклонён" — one access outage of the last month. */
function outageText(o) {
  const what = o.category === 'auth' ? t('data.accessDenied') : t('data.sourceDidNotRespond');
  if (!o.end) return t('data.fromValueUntilNowValue', { value1: shortDate(o.start), value2: what });
  const a = shortDate(o.start);
  const b = shortDate(o.end);
  return `${a === b ? a : `${a} – ${b}`}, ${what}`;
}

const dateLabel = (iso) =>
  iso ? localeDate(new Date(iso), { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

/**
 * The phrase at the top: is there access, and what stands because there is
 * not. Built from the connection's status, its last check and, for an
 * account-based source, the connected account's state.
 */
function headline(conn, feedCount) {
  const grant = conn.oauth_grant;
  const feeds = feedCount
    ? t('data.valueValueReadFromThisSource', { value1: feedCount, value2: plural(feedCount, [t('data.pipeline'), t('data.pipelines'), t('data.pipelines2')]) })
    : t('data.noPipelinesReadFromThisSourceYet');
  if (grant && grant.status !== 'active') {
    return {
      tone: 'bad',
      title: grant.status === 'broken' ? t('data.theSourceDeniedAccess') : t('data.accountDisconnected'),
      text: grant.status === 'broken'
        ? t('data.theConnectedAccountNoLongerGrantsAccessItWasRevokedOr')
        : t('data.reconnectTheAccountToResumePipelines'),
      fix: 'reconnect',
    };
  }
  if (conn.status === 'pending') {
    return { tone: 'info', title: t('data.checkingAccess'), text: t('data.thisPageWillRefreshAutomaticallyWhenTheCheckFinishes') };
  }
  if (conn.status === 'error') {
    const paused = conn.paused_pipeline_count
      ? t('data.valueValuePaused', { value1: conn.paused_pipeline_count, value2: plural(conn.paused_pipeline_count, [t('data.pipeline'), t('data.pipelines'), t('data.pipelines2')]) })
      : '';
    // last_error is the probe's raw output ("auth: <traceback>"): support's to
    // read, not the tenant's. Only its category prefix is worded here.
    const category = (conn.last_error || '').split(':', 1)[0].trim();
    return {
      tone: 'bad',
      title: (FAILURE[category] || FAILURE.auth).text,
      text: t('data.theAccessCheckFailedvalueValue', { value1: conn.last_tested_at ? ` ${whenInline(conn.last_tested_at)}` : '', value2: paused }),
      fix: grant ? 'reconnect' : 'edit',
    };
  }
  if (conn.status === 'disabled') {
    return { tone: 'neutral', title: t('data.sourceDisabled'), text: feeds };
  }
  return {
    tone: 'ok',
    title: t('data.accessGranted'),
    text: `${conn.last_tested_at ? t('data.checkedValue', { value1: whenInline(conn.last_tested_at) }) : ''}${feeds}`,
  };
}

/** What this source pulls, from the wizard-shaped config slices the API returns. */
function pulled(conn) {
  if (conn.bigquery_sources) {
    const b = conn.bigquery_sources;
    return [
      [t('data.dataset2'), [b.dataset_project || b.project_id, b.dataset].filter(Boolean).join('.')],
      [t('data.tables'), b.sources.map((s) => s.name || s.table).join(', ')],
      b.lookback_days
        ? [t('data.window'), b.lookback_days === 1
          ? t('data.theLastDayEachRun')
          : t('data.theLastValueValueEachRun', { value1: b.lookback_days, value2: plural(b.lookback_days, [t('data.day'), t('data.days2'), t('data.days3')]) })]
        : null,
    ].filter(Boolean);
  }
  if (conn.appmetrica_sources) {
    const a = conn.appmetrica_sources;
    return [
      [t('data.application'), a.application_id],
      [t('data.tables'), a.sources.map((s) => s.table).join(', ')],
    ];
  }
  if (conn.file_sources) {
    const f = conn.file_sources;
    return [
      [t('data.bucket'), f.s3_bucket],
      [t('data.tables'), f.tables.map((x) => x.table_name).join(', ')],
    ];
  }
  return null;
}

function safeOAuthDraftConfig(conn, config = {}) {
  const properties = conn.connector_type?.config_schema?.properties || {};
  const tokenKey = conn.connector_type?.auth?.config_key;
  const credentialKeys = new Set((conn.configured_fields || []).filter((key) => !Object.prototype.hasOwnProperty.call(conn.visible_config || {}, key)));
  return Object.fromEntries(Object.entries(config || {}).filter(([key]) =>
    key !== tokenKey && !credentialKeys.has(key) && properties[key] && properties[key].format !== 'password'
      && (properties[key].enum || !/password|passwd|secret|token|credential|key|auth|signature|private/i.test(key))
  ));
}

export default function ConnectionDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const themeTokens = useTokens();

  const { data: conn, isLoading, error } = useConnection(id);
  const { data: pipelines } = usePipelines();
  const { data: activity } = useConnectionActivity(id);
  const { data: healthList } = usePipelinesHealth();
  const updateConnection = useUpdateConnection(id);
  const deleteConnection = useDeleteConnection();
  const testConnection = useTestConnection();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editConfig, setEditConfig] = useState({});
  const [editGrantId, setEditGrantId] = useState(null);
  const [oauthError, setOAuthError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Consent reloads the page. Restore only the draft for this connection and
  // consume callback parameters once, so a later refresh cannot reapply a grant.
  const callbackApplied = useRef(false);
  useEffect(() => {
    if (!conn || callbackApplied.current) return;
    callbackApplied.current = true;
    const grant = searchParams.get('oauth_grant');
    const oauthFailure = searchParams.get('oauth_error');
    if (!grant && !oauthFailure) return;
    const auth = conn.connector_type?.auth;
    if (auth?.kind !== 'oauth') return;
    const draft = takeOAuthDraft(auth.provider);
    const ours = draft?.connectionId === id;
    setEditName(ours && typeof draft.name === 'string' ? draft.name : conn.name);
    // Only visible non-credential fields may survive the full redirect.
    setEditConfig(ours ? safeOAuthDraftConfig(conn, draft.config) : {});
    setEditGrantId(grant || (ours ? draft.grantId : conn.oauth_grant?.public_id) || null);
    setOAuthError(oauthFailure);
    setSettingsOpen(true);
    setIsEditing(true);
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('oauth_grant');
    cleaned.delete('oauth_error');
    setSearchParams(cleaned, { replace: true });
  }, [conn, id, searchParams, setSearchParams]);

  const startEdit = () => {
    // File-source connections are edited through the wizard: sources,
    // schedule groups and credentials change TOGETHER in one atomic save.
    if (conn.file_sources) {
      navigate(`/console/connectors/files/${conn.public_id}/edit`);
      return;
    }
    // Same reason for BigQuery: the flat form shows one comma-separated
    // `tables` string, and the column selection, the per-table window and the
    // key columns have nowhere to live in it.
    if (conn.bigquery_sources) {
      navigate(`/console/connectors/bigquery/${conn.public_id}/edit`);
      return;
    }
    // Same for AppMetrica: the tables and their field lists live in the wizard.
    if (conn.appmetrica_sources) {
      navigate(`/console/connectors/appmetrica/${conn.public_id}/edit`);
      return;
    }
    // And for Yandex Direct: the client list, the stream groups and the
    // settings frozen once data is loaded live in the wizard.
    if (conn.connector_type?.slug === 'yandex-direct') {
      navigate(`/console/connectors/direct/${conn.public_id}/edit`);
      return;
    }
    setEditName(conn.name);
    // Empty — only the fields actually typed into are sent, and the API reads
    // every omitted one as "keep". The form shows what is saved today as each
    // field's placeholder (visible_config), or "unchanged" for a credential.
    setEditConfig({});
    setEditGrantId(conn.oauth_grant?.public_id || null);
    setOAuthError(null);
    setFormErrors({});
    setSaveError(null);
    setSettingsOpen(true);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setFormErrors({});
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editName.trim()) {
      setFormErrors({ _name: t('data.enterASourceName') });
      return;
    }
    setFormErrors({});
    setSaveError(null);
    try {
      const savedGrantId = conn.oauth_grant?.public_id || null;
      const oauth = conn.connector_type?.auth?.kind === 'oauth';
      // Re-sending an unchanged grant would revalidate it even for a rename
      // and reject editing a source whose account has become disconnected.
      await updateConnection.mutateAsync({
        name: editName.trim(),
        config: editConfig,
        ...(oauth && editGrantId !== savedGrantId ? { oauth_grant_id: editGrantId } : {}),
      });
      setIsEditing(false);
    } catch (err) {
      if (hasFieldErrors(err)) {
        const fieldErrors = normalizeFieldErrors(err.response.data);
        if (fieldErrors.name) fieldErrors._name = fieldErrors.name;
        // Errors keyed by something the form has no field for go to the save error.
        const properties = conn?.connector_type?.config_schema?.properties || {};
        const known = new Set([...Object.keys(properties), 'name', '_name']);
        const stray = Object.entries(fieldErrors).filter(([key]) => !known.has(key));
        if (stray.length > 0) setSaveError(stray.map(([, message]) => message).join(' '));
        setFormErrors(fieldErrors);
      } else {
        setSaveError(extractApiError(err, t('data.couldNotSavePleaseTryAgain')));
      }
    }
  };

  const confirmDelete = async () => {
    setDeleteError(null);
    try {
      await deleteConnection.mutateAsync(conn.public_id);
      navigate('/console/connectors');
    } catch (err) {
      setDeleteError(extractApiError(err, t('data.couldNotDeleteTheSourcePleaseTryAgain')));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !conn) {
    return (
      <Alert severity="error">
        {extractApiError(error, t('data.couldNotLoadTheSource'))}{' '}
        <Button size="small" onClick={() => navigate('/console/connectors')}>{t('data.backToList')}</Button>
      </Alert>
    );
  }

  const schema = localizeConnectorType(conn.connector_type)?.config_schema || {};
  // The selected account supplies the token; choosing manual entry reveals it.
  const auth = conn.connector_type?.auth?.kind === 'oauth' ? conn.connector_type.auth : null;
  const grantKey = auth && editGrantId ? auth.config_key : null;
  const editSchema = grantKey
    ? {
        ...schema,
        properties: Object.fromEntries(
          Object.entries(schema.properties || {}).filter(([key]) => key !== grantKey)
        ),
        required: (schema.required || []).filter((key) => key !== grantKey),
      }
    : schema;

  const feeds = (pipelines || []).filter((p) => p.source_connection?.public_id === conn.public_id);
  const head = headline(conn, feeds.length);
  const pulledRows = pulled(conn);

  const fixAction = {
    reconnect: (
      <Button variant="contained" size="small" component={RouterLink} to="/console/connectors/accounts">{t('data.reconnect')}</Button>
    ),
    edit: (
      <Button variant="contained" size="small" onClick={startEdit}>{t('data.editSettings')}</Button>
    ),
  }[head.fix];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/console/connectors')}
          size="small"
          color="inherit"
          sx={{ ml: -1 }}
        >{t('data.sources')}</Button>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700}>
            {conn.name}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: themeTokens.inkMuted, mt: 0.25 }}>
            {localizeConnectorType(conn.connector_type)?.name}
          </Typography>
        </Box>

        {!isEditing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AsyncTaskButton
              label={t('data.checkAccess')}
              loadingLabel={t('data.checking')}
              onTrigger={() => testConnection.mutateAsync(id)}
              variant="outlined"
              size="small"
            />
            <Button variant="outlined" size="small" onClick={startEdit}>{t('data.edit')}</Button>
            <IconButton
              size="small"
              aria-label={t('data.moreActions')}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ border: `1px solid ${themeTokens.borderField}`, borderRadius: '8px' }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => { setMenuAnchor(null); setDeleteError(null); setShowDelete(true); }}
                disabled={deleteConnection.isPending}
                sx={{ color: themeTokens.negative }}
              >{t('data.deleteSource')}</MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      <Verdict tone={head.tone} title={head.title} text={head.text} action={fixAction} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Section title={t('data.access')}>
          <Row label={t('data.connectionMethod')}>
            {conn.oauth_grant
              ? t('data.accountValue', { value1: conn.oauth_grant.external_login || conn.oauth_grant.provider })
              : t('data.keyOrPasswordInSettings')}
          </Row>
          <Row label={t('data.connectedBy')}>
            {conn.created_by_name ? `${conn.created_by_name}, ${dateLabel(conn.created_at)}` : dateLabel(conn.created_at)}
          </Row>
          {activity && (
            <Row label={t('data.interruptionsOverValueDays', { value1: activity.days })}>
              {activity.outages.length
                ? activity.outages.map((o) => <div key={o.start}>{outageText(o)}</div>)
                : t('data.none')}
            </Row>
          )}
          {conn.oauth_grant && (
            <Row label={t('data.accounts')}>
              <Box component={RouterLink} to="/console/connectors/accounts" sx={{ color: themeTokens.link, fontWeight: 700, textDecoration: 'none' }}>{t('data.connectedAccounts2')}</Box>
            </Row>
          )}
        </Section>

        {pulledRows && (
          <Section title={t('data.whatWeLoad')}>
            {pulledRows.map(([label, value]) => (
              <Row key={label} label={label}>{value || '—'}</Row>
            ))}
          </Section>
        )}

        {activity?.bytes_billed ? <SpendCard activity={activity} /> : null}

        <Section
          title={t('data.destination')}
          aside={feeds.length ? `${feeds.length} ${plural(feeds.length, [t('data.pipeline'), t('data.pipelines'), t('data.pipelines2')])}` : null}
        >
          {!feeds.length ? (
            <Typography sx={{ fontSize: '0.85rem', color: themeTokens.inkMuted }}>{t('data.dataFromThisSourceIsNotLoadedAnywhereYet')}</Typography>
          ) : (
            feeds.map((p) => (
              <FeedRow
                key={p.public_id}
                pipeline={p}
                health={(healthList || []).find((h) => h.public_id === p.public_id)}
              />
            ))
          )}
        </Section>
      </Box>

      {/* Connection settings, folded away: they are rarely the point of the visit */}
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Box
          component="button"
          type="button"
          onClick={() => setSettingsOpen((v) => !v)}
          aria-expanded={settingsOpen}
          sx={{
            all: 'unset',
            boxSizing: 'border-box',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '0.9rem',
            '&:focus-visible': { outline: `2px solid ${themeTokens.focus}`, outlineOffset: -2 },
          }}
        >{t('data.connectionSettings')}<ExpandMoreIcon
            fontSize="small"
            sx={{ color: themeTokens.iconMuted, transform: settingsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
          />
        </Box>
        <Collapse in={settingsOpen}>
          <Box sx={{ px: 2, pb: 2, maxWidth: 600 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            {isEditing ? (
              <>
                <TextField
                  label={t('data.name')}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  error={!!formErrors._name}
                  helperText={formErrors._name}
                />
                {auth && <Box sx={{ mb: 2.5 }}>
                  <OAuthConnectField
                    auth={auth}
                    connectorTypeSlug={conn.connector_type.slug}
                    grantId={editGrantId}
                    onGrantChange={(value) => {
                      setEditGrantId(value);
                      setEditConfig((previous) => {
                        const next = { ...previous };
                        delete next[auth.config_key];
                        return next;
                      });
                      setFormErrors({});
                    }}
                    resourceValue={editConfig[auth.resource_field] ?? conn.visible_config?.[auth.resource_field]}
                    onResourceChange={(value) => setEditConfig((previous) => ({ ...previous, [auth.resource_field]: value }))}
                    returnTo={`/console/connectors/${id}`}
                    draft={{ connectionId: id, name: editName, config: safeOAuthDraftConfig(conn, editConfig), grantId: editGrantId }}
                    errorCode={oauthError}
                  />
                </Box>}
                <JsonSchemaForm
                  schema={editSchema}
                  value={editConfig}
                  onChange={setEditConfig}
                  errors={formErrors}
                  configuredFields={conn.configured_fields || []}
                  currentValues={conn.visible_config || {}}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={updateConnection.isPending ? <CircularProgress size={14} color="inherit" /> : null}
                    onClick={handleSave}
                    disabled={updateConnection.isPending}
                  >
                    {updateConnection.isPending ? t('data.saving') : t('data.save')}
                  </Button>
                  <Button size="small" color="inherit" onClick={cancelEdit} disabled={updateConnection.isPending}>{t('data.cancel')}</Button>
                </Box>
              </>
            ) : (
              <ConfigReadView
                schema={schema}
                configuredFields={conn.configured_fields || []}
                currentValues={conn.visible_config || {}}
                grantKey={conn.oauth_grant && auth ? auth.config_key : null}
              />
            )}
          </Box>
        </Collapse>
      </Paper>

      <ConfirmActionDialog
        open={showDelete}
        title={conn.pipeline_count ? t('data.cannotDeleteTheSource') : t('data.deleteSource')}
        confirmWord={t('data.delete')}
        confirmLabel={t('data.deleteSource')}
        confirmColor="error"
        blocked={!!conn.pipeline_count}
        loading={deleteConnection.isPending}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setShowDelete(false)}
      >
        {conn.pipeline_count ? (
          <Typography variant="body2">{t('data.source2')}{' '}<strong>{conn.name}</strong>{' '}{t('data.isUsedBy')}{' '}{conn.pipeline_count}{' '}
            {plural(conn.pipeline_count, [t('data.pipeline'), t('data.pipelines'), t('data.pipelines2')])}{t('data.firstDelete')}{conn.pipeline_count === 1 ? t('data.it') : t('data.them')}{t('data.thenYouCanDeleteThisSource')}</Typography>
        ) : (
          <Typography variant="body2">{t('data.source3')}{' '}<strong>{conn.name}</strong>{' '}{t('data.willBeDeletedTogetherWithItsStoredCredentialsThisCannotBe')}</Typography>
        )}
      </ConfirmActionDialog>
    </Box>
  );
}

/** What the source billed over the last month, by day. */
function SpendCard({ activity }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const max = Math.max(...activity.spend.map((d) => d.bytes_billed));
  const last = activity.spend[activity.spend.length - 1];
  return (
    <Section title={t('data.sourceCosts')} aside={t('data.overValueDays', { value1: activity.days })}>
      <Row label={t('data.theSourceScanned')}>
        <b>{gib(activity.bytes_billed)} {usd(activity.bytes_billed)}</b>
      </Row>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: 56, pt: 1 }} aria-hidden="true">
        {activity.spend.map((d) => (
          <Box
            key={d.date}
            title={`${shortDate(d.date)}: ${gib(d.bytes_billed)}`}
            sx={{
              flex: 1,
              maxWidth: 18,
              minHeight: 3,
              height: `${Math.max(6, (d.bytes_billed / max) * 100)}%`,
              borderRadius: '3px 3px 0 0',
              bgcolor: themeTokens.info,
              opacity: d === last ? 1 : 0.5,
            }}
          />
        ))}
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: themeTokens.inkMuted, mt: 1 }}>{t('data.estimateBasedOnBigqueryOnDemandPricingTheLastBarIs')}{' '}{shortDate(last.date)}, {gib(last.bytes_billed)} {usd(last.bytes_billed)}.
      </Typography>
    </Section>
  );
}

/** One load fed by this source; the whole row opens it. */
function FeedRow({ pipeline, health }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const state = loadState(pipeline, health);
  const last = pipeline.last_run;
  const parts = [
    pipeline.warehouse?.name ? t('data.inWarehouseValue', { value1: pipeline.warehouse.name }) : null,
    scheduleText(pipeline.schedule_cron, undefined, pipeline.schedule_timezone),
    last ? t('data.lastValue', { value1: whenInline(last.finished_at || last.started_at || last.created_at) }) : null,
  ].filter(Boolean);
  return (
    <Box
      component={RouterLink}
      to={`/console/pipelines/${pipeline.public_id}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        color: 'inherit',
        textDecoration: 'none',
        borderTop: `1px solid ${themeTokens.border}`,
        '&:first-of-type': { borderTop: 'none', pt: 0 },
        '&:hover .feed-name': { textDecoration: 'underline' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography className="feed-name" noWrap sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
          {pipeline.name}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted }}>{parts.join(' · ')}</Typography>
      </Box>
      {state.key !== 'ok' && <Chip size="small" label={STATE[state.key].label} color={STATE[state.key].chip} />}
      <ChevronRightIcon fontSize="small" sx={{ color: themeTokens.iconMuted }} />
    </Box>
  );
}

/**
 * Read-only view: every field's current value, except the credentials.
 *
 * `currentValues` is the API's visible_config — the values the backend judged
 * safe to hand back. A field missing from it but present in `configuredFields`
 * is a credential: it is reported as set, never printed. That split is decided
 * server-side on purpose, so this card cannot start guessing which of a custom
 * connector's fields hold a secret.
 */
function ConfigReadView({ schema, configuredFields, currentValues = {}, grantKey = null }) {
  const { t } = useTranslation();
  const properties = schema?.properties || {};
  if (Object.keys(properties).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">{t('data.noSettings')}</Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {Object.entries(properties).map(([key, prop]) => {
        const isPassword = prop.format === 'password';
        const isConfigured = configuredFields.includes(key);
        const text = formatConfigValue(currentValues[key]);
        return (
          <Box
            key={key}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {prop.title || key}
            </Typography>
            {key === grantKey ? (
              // supplied by the connected account at probe and run time
              <Chip label={t('data.fromTheConnectedAccount')} size="small" color="info" />
            ) : text ? (
              <ConfigValue text={text} />
            ) : (
              <Typography variant="body2" fontWeight={isConfigured ? 500 : 400}>
                {isPassword ? (isConfigured ? '••••••••' : '—') : isConfigured ? t('data.set') : '—'}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/** A value, clamped to three lines — a long list must not run the card off. */
function ConfigValue({ text }) {
  useTranslation();
  const value = (
    <Typography
      variant="body2"
      fontWeight={500}
      sx={{
        textAlign: 'right',
        wordBreak: 'break-word',
        // minWidth:0 lets the value give way inside the flex row instead of
        // pushing the card wide enough to scroll the page sideways.
        minWidth: 0,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {text}
    </Typography>
  );
  return text.length > 80 ? <Tooltip title={text}>{value}</Tooltip> : value;
}
